import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { gql } from "graphql-request";
import { useState } from "react";

import {
  asPort,
  asString,
  loopbackCallbackUrl,
} from "../shared/lib/cli-login-search";
import { gqlClient } from "../shared/lib/gql-client";
import { gqlErrorMessage } from "../shared/lib/gql-error";
import { AppHeader } from "../shared/ui/AppHeader";
import { Button } from "../shared/ui/Button";

/**
 * The browser half of `admin login`.
 *
 * The CLI opens this page with a code it minted and the localhost port it is
 * listening on. Sitting under `_authenticated` does the important work: an
 * unauthenticated visitor is redirected to `/auth` and lands back here
 * afterwards, so "sign in first" needs no code of its own.
 *
 * ## Why approval is a button rather than an effect
 *
 * Landing on this URL must not, by itself, hand a terminal a credential. A link
 * in an email that silently minted a 90-day token for whoever clicked it is the
 * failure this page exists to avoid — so the code registers on load, and a
 * person presses Approve.
 *
 * The redirect back to `127.0.0.1` carries only the code the CLI already knew.
 * The token never touches the browser: the CLI trades that code for it directly.
 */

const CREATE_TICKET = gql`
  mutation CreateLoginTicket($input: CreateLoginTicketInput!) {
    createLoginTicket(input: $input)
  }
`;

const APPROVE_TICKET = gql`
  mutation ApproveLoginTicket($input: ApproveLoginTicketInput!) {
    approveLoginTicket(input: $input)
  }
`;

interface CliLoginSearch {
  code: string;
  port: string;
  label: string;
}

export const Route = createFileRoute("/_authenticated/cli-login")({
  /*
   * Validated rather than read raw: these three drive a mutation and a
   * redirect, and a missing one should fail here with something readable rather
   * than reach the server as "undefined".
   */
  validateSearch: (search: Record<string, unknown>): CliLoginSearch => ({
    code: asString(search.code),
    port: asPort(search.port),
    label: asString(search.label) || "CLI",
  }),
  component: CliLoginPage,
});

function CliLoginPage() {
  const { code, port, label } = Route.useSearch();
  const register = useRegisterTicket(code, port, label);
  const { approve, approved } = useApproveTicket(code, port);

  if (!code || !port) {
    return (
      <Shell>
        <p className="text-sm text-content-muted">
          This page is opened by the <code>admin login</code> command. It needs
          a code and a port, and this link has neither — start the flow from
          your terminal.
        </p>
      </Shell>
    );
  }

  if (approved) {
    return (
      <Shell>
        <p className="text-sm text-content-muted" aria-live="polite">
          Approved. Sending you back to the terminal — you can close this tab.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <ApprovalPanel label={label} register={register} approve={approve} />
    </Shell>
  );
}

/**
 * Registers the ticket and fetches the verification code to show.
 *
 * A query rather than a mutation fired from an effect: registering is
 * idempotent server-side (`onConflictDoNothing` on the code hash) and its
 * result is data the page displays, so the query cache's de-duplication does
 * what a ref guarding React's double-invoked effects used to. Never refetched —
 * the code for a ticket does not change.
 */
function useRegisterTicket(code: string, port: string, label: string) {
  return useQuery({
    queryKey: ["cli-login-ticket", code, port, label],
    queryFn: () =>
      gqlClient.request<{ createLoginTicket: string | null }>(CREATE_TICKET, {
        input: { code, redirectPort: port, label },
      }),
    enabled: Boolean(code && port),
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

type RegisterQuery = ReturnType<typeof useRegisterTicket>;

/**
 * Approves, then hands the code back to the listener the CLI opened. A plain
 * navigation rather than a fetch: the listener answers with a page saying it
 * worked, which is the confirmation the person sees.
 */
function useApproveTicket(code: string, port: string) {
  const [approved, setApproved] = useState(false);
  const approve = useMutation({
    mutationFn: () => gqlClient.request(APPROVE_TICKET, { input: { code } }),
    onSuccess: () => {
      setApproved(true);
      const target = loopbackCallbackUrl(port, code);
      if (target) window.location.href = target;
    },
  });
  return { approve, approved };
}

type ApproveMutation = ReturnType<typeof useApproveTicket>["approve"];

function ApprovalPanel({
  label,
  register,
  approve,
}: {
  label: string;
  register: RegisterQuery;
  approve: ApproveMutation;
}) {
  const verification = register.data?.createLoginTicket ?? null;

  return (
    <>
      <p className="text-sm text-content-muted">
        A command-line client called <strong>{label}</strong> is asking to sign
        in as you. Approving mints a token that acts as your account for 90
        days.
      </p>

      {/*
       * The check that makes this page safe to click. A link someone else sent
       * carries *their* code, so the value here would not be the one the
       * person's own terminal printed — and comparing the two is the only
       * thing that separates the two cases.
       */}
      <div className="mt-4 rounded-md bg-surface-muted px-3 py-2">
        <p className="text-sm text-content-muted">
          Your terminal should be showing this code:
        </p>
        <VerificationCode value={verification} />
      </div>

      <p className="mt-4 text-sm text-content-muted">
        <strong>If it does not match, close this tab.</strong> Someone else may
        have sent you this link, and approving it would give them access to your
        account.
      </p>

      <ActionError
        error={register.error}
        prefix="Could not register this login:"
      />
      <ActionError error={approve.error} prefix="Could not approve:" />

      <ApproveButton approve={approve} ready={verification !== null} />
    </>
  );
}

function VerificationCode({ value }: { value: string | null }) {
  return (
    <p className="mt-1 font-mono text-xl tracking-widest" aria-live="polite">
      {value ?? "…"}
    </p>
  );
}

/** Enabled only once the code to compare is on screen. */
function ApproveButton({
  approve,
  ready,
}: {
  approve: ApproveMutation;
  ready: boolean;
}) {
  return (
    <Button
      type="button"
      size="sm"
      className="mt-6"
      disabled={!ready || approve.isPending}
      onClick={() => {
        approve.mutate();
      }}
    >
      {approve.isPending ? "Approving…" : "Approve"}
    </Button>
  );
}

function ActionError({
  error,
  prefix,
}: {
  error: Error | null;
  prefix: string;
}) {
  if (!error) return null;
  return (
    <p
      role="alert"
      className="mt-4 rounded-md bg-surface-muted px-3 py-2 text-sm"
    >
      {prefix} {gqlErrorMessage(error, "the server did not say why")}
    </p>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <AppHeader />
      <main className="mx-auto max-w-lg p-6">
        <h1 className="text-2xl font-semibold">
          Sign in a command-line client
        </h1>
        <div className="mt-4">{children}</div>
      </main>
    </>
  );
}
